import { defineComponent as _defineComponent } from 'vue'
import { inject, reactive, ref, toRefs, watchEffect } from 'vue';
import { formRules } from '@/helpers/tools';
import { userIsStaff } from '@/helpers/auth';
import { instance as api } from '@/helpers/api';
import { getStore } from '@/store';
import { Event } from '@/models/Event';
import { EventOrganizer, EventOrganizerCollection } from '@/models/EventOrganizer';
import AddressComboListItem from '@/components/address/AddressComboListItem.vue';
import EditDialog from '@/components/event_organizer/EditDialog.vue';
import { Address } from '@/models/Address';


export default /*#__PURE__*/_defineComponent({
  __name: 'EventOrganizerSelect',
  props: {
    event: {
        type: Event,
        required: false,
        default: null,
    },
    showEdit: {
        type: Boolean,
        default: true,
    },
    showNew: {
        type: Boolean,
        default: false,
    },
    required: {
        type: Boolean,
        default: true,
    },
},
  emits: ['selected'],
  setup(__props, { emit }) {

const props = __props;


const { event, showEdit } = toRefs(props);

const eoCollection = new EventOrganizerCollection();
if (getEditMode() === 'multi') {
    eoCollection.query();
}

const state = reactive({
    eventOrganizerCollection: eoCollection,
    mode: getEditMode(),
    eoLoading: false,
    showDlgWritable: false,
    showEditDlg: false,
});

const eoToEdit = ref<EventOrganizer | null>(null);

// This component may be part of a form component: We want to check
// for form props like validity and disabled state:
const form: any = inject('form', null);


const textfieldDisplayValue = ref('');

watchEffect(async () => {
    // Whenever the organizer changes, we load it, so that we can pre-select
    // the autocomplete store.
    if (props.event?.organizer_id && eoToEdit.value?.id !== props.event?.organizer_id) {
        const eo = await loadEoForId(props.event?.organizer_id);
        eoToEdit.value = eo as EventOrganizer | null;
        // we add the eo to the autocomplete store, so that we can display it
        // in the text field when clicked into:
        if (eo) {
            if (!state.eventOrganizerCollection.find((entry: EventOrganizer) => entry.id === eo.id)) {
                state.eventOrganizerCollection.add(eo);
            }
        }
    }

    // We need to set the textfield's display value, depending on the type of event.event_organizer's value:
    if (typeof props.event?.event_organizer === 'string') {
        textfieldDisplayValue.value = props.event?.event_organizer;
    } else if (props.event?.event_organizer?.displayTitle) {
        textfieldDisplayValue.value = props.event?.event_organizer?.displayTitle;
    } else {
        textfieldDisplayValue.value = '';
    }
});

/**
 * The field knows 3 different entry modes:
 * - single: if the actual user is an EventOrganizer without OrganizerGroup, it can only show its own EO, no edit.
 * - multi: if the actual user is an EventOrganizer WITH OrganizerGroup, it can select from its assigned EOs
 * - staff: Staff members can see all EOs
 */
function getEditMode(): 'single' | 'multi' | 'staff' {
    const store = getStore();
    if (userIsStaff()) {
        return 'staff';
    }
    if (store.state.user && store.state.user.record.organizer_group_id > 0) {
        return 'multi';
    }
    return 'single';
}

async function editEo() {
    const eo = eoToEdit.value;
    if (eo) {
        const isWritable = await loadIsWritable(eo);
        // eoToEdit.value = eo;
        state.showDlgWritable = isWritable;
        state.showEditDlg = true;
    }
}

async function loadIsWritable(eo: EventOrganizer) {
    const res = await api().get('/EventOrganizer/isWritable', {
        params: {
            id: eo.id,
        },
    });
    return res?.data?.writable === true;
}

async function eventOrganizerChanged(data: { address: Address; eventOrganizer: EventOrganizer }) {
    const eo = await loadEoForId(data.eventOrganizer?.getId());
    if (eo) {
        emit('selected', eo);
    }
    return eo;
}

function onSelect(ev: any) {
    // Note: We need a setTimeout here:
    // when the selected event is triggered,
    // the field's validation state has not yet
    // changed: That means, the new value will be propagated,
    // but the field (and so the surrounding form) is still
    // marked as invalid, until in one of the next n run loops.
    // So we give things some time to settle here.
    // I _guess_ this is a vuetify bug/inconsistency.
    window.setTimeout(() => {
        emit('selected', ev);
    }, 50);
}

async function loadEoForId(id: number) {
    if (!id) {
        return null;
    }
    const collection = new EventOrganizerCollection();
    await collection.query({ id });
    return collection.first();
}

function newOrganizer() {
    eoToEdit.value = new EventOrganizer();
    newEo();
}

function newEo() {
    const eo = eoToEdit.value;
    if (eo) {
        const isWritable = true;
        state.showDlgWritable = isWritable;
        state.showEditDlg = true;
    }
}

return { __sfc: true,props, event, showEdit, eoCollection, state, eoToEdit, form, emit, textfieldDisplayValue, getEditMode, editEo, loadIsWritable, eventOrganizerChanged, onSelect, loadEoForId, newOrganizer, newEo, formRules, AddressComboListItem, EditDialog }
}

})